import superagent from 'superagent';
import config from '../../client-config.js';
import { openPopup, waitForRedirectCallback } from 'helpers/popup.jsx';

const clientAuthBaseUrl = config.clientAuthBaseUrl;
const headerKey = 'x-bambuser-token';

const isNavigationNotImplementedError = (error) =>
  Boolean(error?.message?.toLowerCase().includes('not implemented: navigation'));

const navigateTopWindow = (url) => {
  if (!url) return;

  try {
    window.top.location.href = url;
  } catch (error) {
    if (!isNavigationNotImplementedError(error)) {
      throw error;
    }
  }
};

export const getAuthUser = async (idToken) => {
  try {
    const response = await superagent
      .get(`${clientAuthBaseUrl}/authUser`)
      .set('Accept', 'application/json')
      .set('Authorization', `Bearer ${idToken}`);

    return response.body;
  } catch (err) {
    throw err;
  }
};

export const resetPassword = async (newPassword, token) => {
  try {
    await superagent
      .post(`${clientAuthBaseUrl}/resetPassword/complete`)
      .set(headerKey, token)
      .send({ password: newPassword });
  } catch (err) {
    throw err;
  }
};

export const signIn = async (token) => {
  try {
    const response = await superagent
      .post(`${clientAuthBaseUrl}/signin/passwordless`)
      .set(headerKey, token)
      .send();

    return response.body;
  } catch (err) {
    throw err;
  }
};

export const verifyEmail = async (token) => {
  try {
    await superagent
      .post(`${clientAuthBaseUrl}/verifyEmail/complete`)
      .set(headerKey, token)
      .send();
  } catch (err) {
    throw err;
  }
};

export const sendEmailVerification = async (idToken, actionCodeSettings) => {
  try {
    await superagent
      .post(`${clientAuthBaseUrl}/verifyEmail/send`)
      .set('Authorization', `Bearer ${idToken}`)
      .send(actionCodeSettings);
  } catch (err) {
    throw err;
  }
};

export const signInWithMicrosoft = async () => {
  const data = {
    providerId: 'microsoft',
    continueUri: `${window.location.origin}${window.location.pathname}`,
    customParameter: {
      prompt: 'select_account',
    },
  };
  try {
    const response = await superagent
      .post(`${clientAuthBaseUrl}/sso/oidc/start`)
      .send(data);
    window.sessionStorage.setItem('_bamls_auth_sid', response.body.sessionId);
    navigateTopWindow(response.body.authUri);
  } catch (err) {
    throw err;
  }
};

const linkMicrosoftInPopup = async (firebaseIdToken) => {
  const data = {
    providerId: 'microsoft',
    continueUri: `${window.location.origin}/redirect-callback`,
    customParameter: {
      prompt: 'select_account',
    },
  };
  try {
    const popup = openPopup({ target: 'MicrosoftAuth', features: 'width=400,height=600' });
    const response = await superagent
      .post(`${clientAuthBaseUrl}/sso/oidc/start?linking=true`)
      .send(data);
    window.sessionStorage.setItem('_bamls_auth_sid', response.body.sessionId);

    popup.location.href = response.body.authUri;
    try {
      const uri = await waitForRedirectCallback(popup, 'MicrosoftAuth');
      // This does linking because we pass firebase token and set linking in state on the start request
      await authenticateWithCustomSSOLink(uri, firebaseIdToken);
    } catch (err) {
      console.error('Failed to link with popup', err);
    }
  } catch (err) {
    throw err;
  }
};

export const linkMicrosoftAccount = async (firebaseIdToken, microsoftIdToken) => {
  try {
    await superagent
      .post(`${clientAuthBaseUrl}/sso/link`)
      .set('Accept', 'application/json')
      .set('Authorization', `Bearer ${firebaseIdToken}`)
      .send({ providerId: 'microsoft', idToken: microsoftIdToken });
  } catch (err) {
    throw err;
  }
};

export const unlinkMicrosoftAccount = async (firebaseIdToken) => {
  try {
    await superagent
      .post(`${clientAuthBaseUrl}/sso/unlink`)
      .set('Accept', 'application/json')
      .set('Authorization', `Bearer ${firebaseIdToken}`)
      .send({ providerId: 'microsoft' });
  } catch (err) {
    throw err;
  }
};

export class SSOEmailConflictError extends Error {
  constructor(email, idToken, provider) {
    super();
    this.idToken = idToken;
    this.email = email;
    this.provider = provider;
    this.name = this.constructor.name;
    Error.captureStackTrace(this, this.constructor);
  }
}

export const authenticateWithCustomSSOLink = async (authUri, firebaseIdToken) => {
  const data = {
    sessionId: window.sessionStorage.getItem('_bamls_auth_sid'),
    requestUri: authUri,
  };
  try {
    let request = superagent
      .post(`${clientAuthBaseUrl}/sso/oidc/complete`);
    if (firebaseIdToken) request = request.set('Authorization', `Bearer ${firebaseIdToken}`);
    const response = await request.send(data);
    return response.body;
  } catch (e) {
    /**@type {import('superagent').ResponseError} */
    const err = e;
    if (err.status === 409 && err.response.body?.code === 'user-email-already-exists') {
      const { providerId, email, idToken } = err.response.body;
      throw new SSOEmailConflictError(email, idToken, providerId);
    } else {
      throw e;
    }
  }
};

export const lookupEmailDomain = async (email) => {
  try {
    const domain = email.split('@')[1].toLowerCase();
    const response = await superagent
      .get(`${clientAuthBaseUrl}/sso/saml/config?domain=${domain}`)
      .send();
    if (response.status === 200) {
      return { provider: 'saml', disableOtherLoginMethods: response?.body?.disableOtherLoginMethods };
    }
  } catch (e) {
    if (e.status !== 404) {
      // 404 means saml is not configured for this domain.
      // Consider other errors as fatal.
      // TODO: consider throwing?
      console.error('Failed to lookup email domain config', e.message);
    }
  }
  return {};
};

export const startSignInWithSAML = async (email, nextUri) => {
  const response = await superagent
    .post(`${clientAuthBaseUrl}/sso/saml/start?platform=web&email=${email}`)
    .send();
  window.sessionStorage.setItem('_bamls_auth_sid', response.body.sessionId);
  window.sessionStorage.setItem('_bamls_auth_redirect_uri', nextUri);
  navigateTopWindow(response.body.authUri);
};

export default {
  getAuthUser,
  resetPassword,
  signIn,
  verifyEmail,
  sendEmailVerification,
  signInWithMicrosoft,
  linkMicrosoftInPopup,
  startSignInWithSAML,
  lookupEmailDomain,
  authenticateWithCustomSSOLink,
  linkMicrosoftAccount,
  unlinkMicrosoftAccount,
  SSOEmailConflictError,
};
